import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { 
  Plane, 
  Navigation, 
  Globe, 
  Radar, 
  MapPin, 
  Wrench, 
  Clock, 
  Shield, 
  Menu, 
  X,
  Send,
  ChevronRight,
  Languages,
  CheckCircle2,
  Circle,
  ArrowLeft,
  GitBranch
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import { languageNames, languageFlags } from "./i18n/translations";

// AzorPort platform URL
const AZORPORT_URL = "https://app.azorport.com";

// Airport data
const airports = [
  { code: "PDL", name: "Ponta Delgada", island: "São Miguel" },
  { code: "TER", name: "Lajes", island: "Terceira" },
  { code: "HOR", name: "Horta", island: "Faial" },
  { code: "PIX", name: "Pico", island: "Pico" },
  { code: "SJZ", name: "São Jorge", island: "São Jorge" },
  { code: "GRW", name: "Graciosa", island: "Graciosa" },
  { code: "FLW", name: "Flores", island: "Flores" },
  { code: "CVU", name: "Corvo", island: "Corvo" },
  { code: "SMA", name: "Santa Maria", island: "Santa Maria" },
];

// Language Switcher Component
const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();
  const languages = ['pt', 'en', 'es', 'fr', 'de'];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors border border-slate-200 hover:border-slate-300 bg-white"
          data-testid="language-switcher"
        >
          <Languages className="w-4 h-4" />
          <span className="uppercase">{language}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {languages.map((lang) => (
          <DropdownMenuItem 
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`cursor-pointer ${language === lang ? 'bg-slate-100' : ''}`}
            data-testid={`lang-${lang}`}
          >
            <span className="mr-2">{languageFlags[lang]}</span>
            {languageNames[lang]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Navigation Component
const NavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav className="nav-glass sticky top-0 z-50 w-full border-b border-slate-200/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a 
            href="/" 
            className="flex items-center gap-2"
            data-testid="nav-logo"
          >
            <Plane className="w-6 h-6 text-[#002FA7]" />
            <span className="font-heading font-semibold text-xl text-slate-900">AzorPort</span>
            <span className="ml-1 px-2 py-0.5 text-[10px] font-mono font-semibold bg-amber-400 text-amber-900 rounded-sm" data-testid="beta-badge">
              {t('beta.badge')}
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('about')}
              className="text-slate-600 hover:text-slate-900 transition-colors font-medium text-sm"
              data-testid="nav-about"
            >
              {t('nav.about')}
            </button>
            <button 
              onClick={() => scrollToSection('platform')}
              className="text-slate-600 hover:text-slate-900 transition-colors font-medium text-sm"
              data-testid="nav-platform"
            >
              {t('nav.platform')}
            </button>
            <button 
              onClick={() => scrollToSection('coverage')}
              className="text-slate-600 hover:text-slate-900 transition-colors font-medium text-sm"
              data-testid="nav-coverage"
            >
              {t('nav.coverage')}
            </button>
          </div>

          {/* Right Side: Language + CTA */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <a 
              href={AZORPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary rounded-none px-6 py-2 font-medium inline-flex items-center justify-center text-sm text-white bg-[#002FA7] hover:bg-[#00227A] transition-colors"
              data-testid="nav-cta"
            >
              {t('nav.requestDemo')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => scrollToSection('about')}
                className="text-slate-600 hover:text-slate-900 text-left py-2"
                data-testid="mobile-nav-about"
              >
                {t('nav.about')}
              </button>
              <button 
                onClick={() => scrollToSection('platform')}
                className="text-slate-600 hover:text-slate-900 text-left py-2"
                data-testid="mobile-nav-platform"
              >
                {t('nav.platform')}
              </button>
              <button 
                onClick={() => scrollToSection('coverage')}
                className="text-slate-600 hover:text-slate-900 text-left py-2"
                data-testid="mobile-nav-coverage"
              >
                {t('nav.coverage')}
              </button>
              <div className="py-2">
                <LanguageSwitcher />
              </div>
              <a 
                href={AZORPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary rounded-none w-full mt-2 inline-flex items-center justify-center py-2 text-sm text-white bg-[#002FA7] hover:bg-[#00227A] transition-colors"
                data-testid="mobile-nav-cta"
              >
                {t('nav.requestDemo')}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Beta Banner Component
const BetaBanner = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-amber-50 border-b border-amber-200" data-testid="beta-banner">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-2.5">
        <p className="text-center text-sm text-amber-800">
          <span className="inline-flex items-center gap-2">
            <span className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-amber-400 text-amber-900 rounded-sm">
              {t('beta.badge')}
            </span>
            {t('beta.banner')}
          </span>
        </p>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section className="hero-gradient py-16 sm:py-24 lg:py-32" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="animate-fade-in">
            <p className="overline mb-4">{t('hero.overline')}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 tracking-tight leading-tight mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={AZORPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary rounded-none px-8 py-3 font-medium text-base inline-flex items-center justify-center text-white bg-[#002FA7] hover:bg-[#00227A] transition-colors"
                data-testid="hero-cta-primary"
              >
                {t('hero.ctaPrimary')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </a>
              <Button 
                variant="outline"
                onClick={() => document.getElementById('platform').scrollIntoView({ behavior: 'smooth' })}
                className="btn-outline rounded-none px-8 py-3 font-medium text-base"
                data-testid="hero-cta-secondary"
              >
                {t('hero.ctaSecondary')}
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div className="animate-fade-in animate-delay-200">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1732658799998-8eba1ea7eaf5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhaXJwb3J0JTIwcnVud2F5JTIwYWlycGxhbmV8ZW58MHx8fHwxNzc1NTcwOTA5fDA&ixlib=rb-4.1.0&q=85"
                alt="Aviation"
                className="w-full h-[400px] lg:h-[500px] object-cover shadow-2xl"
                data-testid="hero-image"
              />
              {/* Stats Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm p-6 border-t border-slate-200">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="font-heading font-semibold text-2xl text-[#002FA7]">9</p>
                    <p className="text-xs text-slate-600 mt-1">{t('hero.stats.airports')}</p>
                  </div>
                  <div className="text-center border-x border-slate-200">
                    <p className="font-heading font-semibold text-2xl text-[#002FA7]">24/7</p>
                    <p className="text-xs text-slate-600 mt-1">{t('hero.stats.monitoring')}</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading font-semibold text-2xl text-[#002FA7]">100%</p>
                    <p className="text-xs text-slate-600 mt-1">{t('hero.stats.integration')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();

  return (
    <section id="about" className="py-24 sm:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <img 
              src="https://images.unsplash.com/photo-1531264167393-c9ed198c28ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxhem9yZXMlMjBhZXJpYWwlMjBpc2xhbmR8ZW58MHx8fHwxNzc1NTcwOTA5fDA&ixlib=rb-4.1.0&q=85"
              alt="Azores"
              className="w-full h-[400px] lg:h-[500px] object-cover"
              data-testid="about-image"
            />
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <p className="overline mb-4">{t('about.overline')}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight mb-6">
              {t('about.title')}
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>{t('about.paragraph1')}</p>
              <p>{t('about.paragraph2')}</p>
              <p>{t('about.paragraph3')}</p>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-200">
              <div className="flex items-center gap-8">
                <div>
                  <p className="font-heading font-semibold text-3xl text-slate-900">2026</p>
                  <p className="text-sm text-slate-500">{t('about.founded')}</p>
                </div>
                <div className="w-px h-12 bg-slate-200"></div>
                <div>
                  <p className="font-heading font-semibold text-3xl text-slate-900">9</p>
                  <p className="text-sm text-slate-500">{t('about.islandsServed')}</p>
                </div>
                <div className="w-px h-12 bg-slate-200"></div>
                <div>
                  <p className="font-heading font-semibold text-3xl text-slate-900">1</p>
                  <p className="text-sm text-slate-500">{t('about.platformLabel')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section (Detailed Modules)
const FeaturesSection = () => {
  const { t } = useLanguage();

  const modules = [
    { key: 'radar', icon: Radar, highlight: true },
    { key: 'traffic', icon: Navigation },
    { key: 'maintenance', icon: Wrench },
    { key: 'operations', icon: Clock, dark: true },
    { key: 'security', icon: Shield },
    { key: 'connectivity', icon: Globe },
  ];

  return (
    <section id="platform" className="py-24 sm:py-32 bg-slate-50" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="overline mb-4">{t('features.overline')}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight mb-4">
            {t('features.title')}
          </h2>
          <p className="text-slate-600 leading-relaxed">
            {t('features.description')}
          </p>
        </div>

        {/* Features Grid - 2 columns on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {modules.map((module, index) => {
            const IconComponent = module.icon;
            const isDark = module.dark;
            const isHighlight = module.highlight;
            
            return (
              <div 
                key={module.key}
                className={`bento-card p-8 ${
                  isDark 
                    ? 'bg-[#002FA7] text-white' 
                    : isHighlight
                    ? 'bg-white border-2 border-[#002FA7] shadow-lg'
                    : 'bg-white border border-slate-200'
                }`}
                data-testid={`feature-${module.key}`}
              >
                <div className="flex items-start gap-6">
                  <div className={`w-14 h-14 flex-shrink-0 flex items-center justify-center ${
                    isDark ? 'bg-white/20' : 'bg-[#002FA7]/10'
                  }`}>
                    <IconComponent className={`w-7 h-7 ${isDark ? 'text-white' : 'text-[#002FA7]'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-heading text-xl font-medium mb-3 ${
                      isDark ? 'text-white' : 'text-slate-900'
                    }`}>
                      {t(`features.items.${module.key}.title`)}
                    </h3>
                    <p className={`leading-relaxed ${
                      isDark ? 'text-white/85' : 'text-slate-600'
                    }`}>
                      {t(`features.items.${module.key}.description`)}
                    </p>
                    {module.key === 'operations' && (
                      <div className="mt-6 pt-6 border-t border-white/20">
                        <p className="font-mono text-3xl text-white font-semibold">99.9%</p>
                        <p className="text-xs text-white/60 mt-1">{t('features.uptime')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Coverage Section (9 Airports)
const CoverageSection = () => {
  const { t } = useLanguage();

  return (
    <section id="coverage" className="py-24 sm:py-32 bg-white" data-testid="coverage-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="overline mb-4">{t('coverage.overline')}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight mb-4">
            {t('coverage.title')}
          </h2>
          <p className="text-slate-600 leading-relaxed">
            {t('coverage.description')}
          </p>
        </div>

        {/* Airports Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {airports.map((airport) => (
            <div 
              key={airport.code}
              className="airport-card bg-white p-6 cursor-pointer group"
              data-testid={`airport-${airport.code.toLowerCase()}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="airport-code font-mono text-3xl font-semibold text-slate-900 mb-1 transition-colors">
                    {airport.code}
                  </p>
                  <p className="airport-name text-slate-600 font-medium transition-colors">
                    {airport.name}
                  </p>
                  <p className="airport-name text-sm text-slate-400 mt-1 transition-colors">
                    {airport.island}
                  </p>
                </div>
                <MapPin className="w-5 h-5 text-slate-400 group-hover:text-white/80 transition-colors" />
              </div>
            </div>
          ))}
        </div>

        {/* Map Indicator */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-500">
            <Globe className="w-4 h-4 inline-block mr-2 -mt-0.5" />
            {t('coverage.location')}
          </p>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        message: formData.message,
        language: language
      });
      
      toast.success(t('contact.toast.success'), {
        description: t('contact.toast.successDescription')
      });
      
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(language === 'pt' ? 'Erro ao enviar mensagem' : 
                  language === 'en' ? 'Failed to send message' :
                  language === 'es' ? 'Error al enviar mensaje' :
                  language === 'fr' ? 'Échec de l\'envoi du message' :
                  'Nachricht konnte nicht gesendet werden', {
        description: language === 'pt' ? 'Por favor, tente novamente mais tarde.' :
                     language === 'en' ? 'Please try again later.' :
                     language === 'es' ? 'Por favor, inténtelo de nuevo más tarde.' :
                     language === 'fr' ? 'Veuillez réessayer plus tard.' :
                     'Bitte versuchen Sie es später erneut.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Content */}
          <div>
            <p className="overline mb-4">{t('contact.overline')}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight mb-6">
              {t('contact.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              {t('contact.description')}
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
                  <Send className="w-5 h-5 text-[#002FA7]" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{t('contact.email')}</p>
                  <p className="text-slate-600">azorport@hotmail.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#002FA7]" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{t('contact.location')}</p>
                  <p className="text-slate-600">{t('contact.locationValue')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 lg:p-12 border border-slate-200">
            <h3 className="font-heading text-xl font-medium text-slate-900 mb-6">
              {t('contact.form.title')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-slate-700 mb-2 block">{t('contact.form.name')}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.form.namePlaceholder')}
                    required
                    className="rounded-none border-slate-300 focus:border-[#002FA7] focus:ring-[#002FA7]"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700 mb-2 block">{t('contact.form.emailLabel')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.emailPlaceholder')}
                    required
                    className="rounded-none border-slate-300 focus:border-[#002FA7] focus:ring-[#002FA7]"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="company" className="text-slate-700 mb-2 block">{t('contact.form.company')}</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder={t('contact.form.companyPlaceholder')}
                  className="rounded-none border-slate-300 focus:border-[#002FA7] focus:ring-[#002FA7]"
                  data-testid="contact-company"
                />
              </div>
              <div>
                <Label htmlFor="message" className="text-slate-700 mb-2 block">{t('contact.form.message')}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.form.messagePlaceholder')}
                  rows={4}
                  required
                  className="rounded-none border-slate-300 focus:border-[#002FA7] focus:ring-[#002FA7] resize-none"
                  data-testid="contact-message"
                />
              </div>
              <Button 
                type="submit"
                className="btn-primary rounded-none w-full py-3 font-medium"
                disabled={isSubmitting}
                data-testid="contact-submit"
              >
                {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="footer-dark py-16 sm:py-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Plane className="w-6 h-6 text-white" />
              <span className="font-heading font-semibold text-xl text-white">AzorPort</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-heading font-medium text-white mb-4">{t('footer.navigation')}</h4>
            <ul className="space-y-2">
              <li>
                <a href="#about" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('nav.about')}
                </a>
              </li>
              <li>
                <a href="#platform" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('nav.platform')}
                </a>
              </li>
              <li>
                <a href="#coverage" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('nav.coverage')}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('contact.overline')}
                </a>
              </li>
              <li>
                <Link to="/roadmap" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('nav.roadmap')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Airports */}
          <div>
            <h4 className="font-heading font-medium text-white mb-4">{t('footer.airports')}</h4>
            <div className="flex flex-wrap gap-2">
              {airports.map((airport) => (
                <span 
                  key={airport.code}
                  className="font-mono text-xs bg-slate-800 text-slate-400 px-2 py-1"
                >
                  {airport.code}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            {t('footer.copyright')}
          </p>
          <p className="text-slate-500 text-sm">
            {t('footer.createdBy')}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <BetaBanner />
      <NavBar />
      <main>
        <HeroSection />
        <AboutSection />
        <FeaturesSection />
        <CoverageSection />
        <ContactSection />
      </main>
      <Footer />
      <Toaster position="top-right" />
    </div>
  );
};

// Roadmap Page
const RoadmapPage = () => {
  const { t, language } = useLanguage();
  const [phases, setPhases] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRoadmap = async () => {
      try {
        const response = await axios.get(`${API}/roadmap`);
        setPhases(response.data.phases || []);
        setChangelog(response.data.changelog || []);
      } catch (error) {
        console.error('Failed to fetch roadmap:', error);
        // Fallback to translations if API fails
      } finally {
        setLoading(false);
      }
    };
    fetchRoadmap();
  }, []);

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    if (status === 'inProgress') return <Circle className="w-5 h-5 text-amber-500 fill-amber-500" />;
    return <Circle className="w-5 h-5 text-slate-300" />;
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return 'border-green-500 bg-green-50';
    if (status === 'inProgress') return 'border-amber-500 bg-amber-50';
    return 'border-slate-200 bg-white';
  };

  const getStatusLabel = (status) => {
    if (status === 'completed') return t('roadmap.sections.completed');
    if (status === 'inProgress') return t('roadmap.sections.inProgress');
    return t('roadmap.sections.planned');
  };

  // Get localized text from multi-language object
  const getLocalizedText = (obj, fallback = '') => {
    if (!obj) return fallback;
    return obj[language] || obj['en'] || obj['pt'] || fallback;
  };

  const getLocalizedArray = (obj, fallback = []) => {
    if (!obj) return fallback;
    return obj[language] || obj['en'] || obj['pt'] || fallback;
  };

  return (
    <div className="min-h-screen bg-white">
      <BetaBanner />
      
      {/* Simple Navbar for Roadmap */}
      <nav className="nav-glass sticky top-0 z-50 w-full border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="roadmap-nav-logo">
              <Plane className="w-6 h-6 text-[#002FA7]" />
              <span className="font-heading font-semibold text-xl text-slate-900">AzorPort</span>
              <span className="ml-1 px-2 py-0.5 text-[10px] font-mono font-semibold bg-amber-400 text-amber-900 rounded-sm">
                {t('beta.badge')}
              </span>
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      <main className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#002FA7] transition-colors mb-8"
            data-testid="roadmap-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('roadmap.backToHome')}
          </Link>

          {/* Header */}
          <div className="mb-16">
            <p className="overline mb-4">{t('roadmap.overline')}</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight mb-4">
              {t('roadmap.title')}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl">
              {t('roadmap.subtitle')}
            </p>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-6 mb-12 pb-8 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="text-sm text-slate-600">{t('roadmap.sections.completed')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="w-5 h-5 text-amber-500 fill-amber-500" />
              <span className="text-sm text-slate-600">{t('roadmap.sections.inProgress')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="w-5 h-5 text-slate-300" />
              <span className="text-sm text-slate-600">{t('roadmap.sections.planned')}</span>
            </div>
          </div>

          {/* Phases */}
          <div className="space-y-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-500 mt-4">Loading...</p>
              </div>
            ) : phases.length > 0 ? (
              phases.map((phase) => (
                <div 
                  key={phase.phase_key}
                  className={`border-l-4 p-6 ${getStatusColor(phase.status)}`}
                  data-testid={`roadmap-${phase.phase_key}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-xl font-medium text-slate-900">
                      {getLocalizedText(phase.title)}
                    </h2>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(phase.status)}
                      <span className={`text-sm font-medium ${
                        phase.status === 'completed' ? 'text-green-600' :
                        phase.status === 'inProgress' ? 'text-amber-600' : 'text-slate-500'
                      }`}>
                        {getStatusLabel(phase.status)}
                      </span>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {getLocalizedArray(phase.items).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          phase.status === 'completed' ? 'bg-green-500' :
                          phase.status === 'inProgress' ? 'bg-amber-500' : 'bg-slate-300'
                        }`} />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              // Fallback to static translations
              ['phase1', 'phase2', 'phase3', 'phase4'].map((phaseKey) => {
                const phase = t(`roadmap.phases.${phaseKey}`);
                return (
                  <div 
                    key={phaseKey}
                    className={`border-l-4 p-6 ${getStatusColor(phase.status)}`}
                    data-testid={`roadmap-${phaseKey}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-xl font-medium text-slate-900">
                        {phase.title}
                      </h2>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(phase.status)}
                        <span className={`text-sm font-medium ${
                          phase.status === 'completed' ? 'text-green-600' :
                          phase.status === 'inProgress' ? 'text-amber-600' : 'text-slate-500'
                        }`}>
                          {getStatusLabel(phase.status)}
                        </span>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {phase.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            phase.status === 'completed' ? 'bg-green-500' :
                            phase.status === 'inProgress' ? 'bg-amber-500' : 'bg-slate-300'
                          }`} />
                          <span className="text-slate-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
            )}
          </div>

          {/* Changelog */}
          <div className="mt-16 pt-16 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-8">
              <GitBranch className="w-6 h-6 text-[#002FA7]" />
              <h2 className="font-heading text-2xl font-medium text-slate-900">
                {t('roadmap.changelog.title')}
              </h2>
            </div>
            <div className="space-y-4">
              {changelog.length > 0 ? (
                changelog.map((entry, idx) => (
                  <div 
                    key={entry.id || idx}
                    className="flex items-start gap-4 p-4 bg-slate-50 border border-slate-200"
                    data-testid={`changelog-entry-${idx}`}
                  >
                    <div className="flex-shrink-0">
                      <span className="font-mono text-sm font-semibold text-[#002FA7] bg-[#002FA7]/10 px-2 py-1">
                        v{entry.version}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-900 font-medium">{getLocalizedText(entry.changes)}</p>
                      <p className="text-sm text-slate-500 mt-1">{getLocalizedText(entry.date)}</p>
                    </div>
                  </div>
                ))
              ) : (
                // Fallback to static translations
                t('roadmap.changelog.entries').map((entry, idx) => (
                  <div 
                    key={idx}
                    className="flex items-start gap-4 p-4 bg-slate-50 border border-slate-200"
                    data-testid={`changelog-entry-${idx}`}
                  >
                    <div className="flex-shrink-0">
                      <span className="font-mono text-sm font-semibold text-[#002FA7] bg-[#002FA7]/10 px-2 py-1">
                        v{entry.version}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-900 font-medium">{entry.changes}</p>
                      <p className="text-sm text-slate-500 mt-1">{entry.date}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <Toaster position="top-right" />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
